import { useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useQueryInvitationUrl } from './useQueryInvitationUrl.tsx';
import { useCreateInvitationUrl } from './useCreateInvitationUrl.tsx';
import { InvitationUrlsStatus } from './__types__/CreateInvitationUrlQuery.ts';

type Source = 'unknown' | 'contextual-invite-nudge' | 'onboarding-account-setup-modal';
/**
 * Custom hook that handles fetching and creating invitation links to be used
 * in the invite page.
 *
 * @returns An object containing the invite link, an optional error, and loading state.
 */

type UseInviteLinkProps = {
	skip?: boolean;
	allowGenerateNewLink?: boolean;
	source?: Source;
};

export const useInviteLink = ({
	skip,
	allowGenerateNewLink,
	source = 'unknown',
}: UseInviteLinkProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hasTrackedUsage = useRef(false);

	const {
		existingInviteLink,
		existingInviteLinkStatus,
		loading: queryInviteLoading,
		error: queryInviteError,
	} = useQueryInvitationUrl({ skipQuery: skip });

	const {
		createNewInviteLink,
		newInviteLink,
		error: createInviteError,
		loading: createInviteLoading,
		called: createInviteCalled,
	} = useCreateInvitationUrl();

	// Track hook usage on mount
	useEffect(() => {
		if (!hasTrackedUsage.current) {
			hasTrackedUsage.current = true;
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'used',
					actionSubject: 'inviteLinkHook',
					source,
					attributes: {
						skip: skip ?? false,
						allowGenerateNewLink: allowGenerateNewLink ?? false,
					},
				},
			}).fire();
		}
	}, [createAnalyticsEvent, source, skip, allowGenerateNewLink]);

	useEffect(() => {
		// If we have an existing invite link (after fetching) and if it is expired, create a new one
		if (
			!skip &&
			!queryInviteLoading &&
			!queryInviteError &&
			((allowGenerateNewLink && !existingInviteLink) ||
				existingInviteLinkStatus === InvitationUrlsStatus.EXPIRED) &&
			!createInviteCalled
		) {
			// Track when creating a new invite link
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'inviteLinkCreated',
					actionSubject: 'inviteLinkHook',
					source,
					attributes: {
						reason:
							existingInviteLinkStatus === InvitationUrlsStatus.EXPIRED
								? 'expired'
								: 'noExistingLink',
					},
				},
			}).fire();
			void createNewInviteLink();
		}
	}, [
		skip,
		queryInviteLoading,
		createNewInviteLink,
		createInviteCalled,
		queryInviteError,
		allowGenerateNewLink,
		existingInviteLink,
		existingInviteLinkStatus,
		source,
		createAnalyticsEvent,
	]);

	const inviteLink =
		(existingInviteLinkStatus === InvitationUrlsStatus.ACTIVE && existingInviteLink) ||
		newInviteLink;

	const error = queryInviteError || createInviteError;

	const loading = queryInviteLoading || createInviteLoading;

	return { inviteLink, error, loading };
};
